import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import uuid from 'react-uuid';
import { create } from 'zustand';

import Spinner from '@/components/Common/Spinner';
import DefaultToast, {
  type DefaultToastProps,
} from '@/components/Toast/DefaultToast';
import colors from '@/styles/colors/color';

import useUIStore, { getSystemTheme } from './UIStore';

type ToastPositionT = 'left' | 'center' | 'right' | 'bottom-right';

export type ToastOptionT = {
  duration?: number;
  position?: ToastPositionT;
};

export type ToastItemT = {
  toastId: string;
  children: ReactNode;
  closeTrigger: boolean;
  duration: number;
  position?: ToastPositionT;
};

type ToastTypeT = 'success' | 'fail' | 'normal';

type ToastIconMap = {
  [key in ToastTypeT]: ReactNode;
};

type State = {
  toasts: ToastItemT[];
};

type Action = {
  addToast: (children: ReactNode, options?: ToastOptionT) => string;
  addDefaultToast: (
    header: string | ReactNode,
    type?: ToastTypeT,
    duration?: number,
    customOptions?: Partial<DefaultToastProps>,
  ) => void;
  popToast: (toastId: string) => void;
  initToastStore: () => void;
};

const DEFAULT_DURATION = 3000;

const initialState: State = {
  toasts: [],
};

const useToastStore = create<State & Action>((set, get) => ({
  ...initialState,
  addToast: (children, options) => {
    const toastId = uuid();
    const newToastItem = {
      children,
      ...options,
      toastId,
      duration: options?.duration || DEFAULT_DURATION,
      closeTrigger: false,
    };
    set((state) => ({
      ...state,
      toasts: [...state.toasts, newToastItem],
    }));
    return toastId;
  },
  addDefaultToast(message, type = 'success', duration, customOptions) {
    const colorThemeSetting = useUIStore.getState().colorMode;
    const systemTheme = getSystemTheme();
    let color = colors;

    if (colorThemeSetting === 'system') {
      color = systemTheme === 'dark' ? colors : colors;
    } else {
      color = colorThemeSetting === 'dark' ? colors : colors;
    }

    const toastIcon: ToastIconMap = {
      success: <CheckCircleOutlined style={{ color: color.green500 }} />,
      fail: <CloseCircleOutlined style={{ color: color.red600 }} />,
      normal: <ExclamationCircleOutlined style={{ color: color.gray600 }} />,
    };
    get().addToast(
      <DefaultToast
        {...{
          header: message,
          Icon: toastIcon[type],
        }}
        {...customOptions}
      />,
      { position: 'center', duration: duration || DEFAULT_DURATION },
    );
  },
  popToast: (toastId: string) => {
    set((state) => {
      const newToasts = [...state.toasts];

      const toastTargetIndex = newToasts.findIndex(
        (item) => item.toastId === toastId,
      );
      if (toastTargetIndex === -1) {
        return {
          ...state,
        };
      }

      newToasts.splice(toastTargetIndex, 1, {
        ...newToasts[toastTargetIndex],
        closeTrigger: true,
      });

      return {
        ...state,
        toasts: newToasts,
      };
    });
    setTimeout(() => {
      set((state) => ({
        ...state,
        toasts: state.toasts.filter((item) => item.toastId !== toastId),
      }));
    }, 300);
  },
  initToastStore() {
    set(initialState);
  },
}));

export const useToast = () => useToastStore((state) => state.addDefaultToast);
export const toast = useToastStore.getState().addDefaultToast;
export const customToast = useToastStore.getState().addToast;

const MAX_LOADING_TOAST_DURATION = 1000 * 60 * 10; // 10 minutes (effectively infinite)

/**
 * Loading toast를 표시하고 dismiss 함수를 반환
 * @returns dismiss 함수 - 호출 시 toast 제거
 */
export const loadingToast = (header: string, body?: string) => {
  const toastId = useToastStore
    .getState()
    .addToast(
      <DefaultToast
        header={header}
        body={body}
        Icon={<Spinner size={16} color={colors.dune0} />}
      />,
      { position: 'center', duration: MAX_LOADING_TOAST_DURATION },
    );
  return () => useToastStore.getState().popToast(toastId);
};

export default useToastStore;
